import React, { useEffect, useMemo, useRef, useState } from 'react';
import Frame from 'react-frame-component';
import { Tabs } from '@opendate-app/setlist';

import WebOrderFormApp from '../../../WebOrderForm/startup/WebOrderFormApp';
import './customWidgetBuilder.css';

// Subpath into the CJS build. The package's `browser` field points at
// index.umd.js, whose UMD wrapper discards the factory return value —
// so a bare `import` / `require('@iframe-resizer/parent')` resolves to
// undefined under Shakapacker 9 / rspack. The CJS file does
// `module.exports = iframeResize` and imports cleanly.
const iframeResize = require('@iframe-resizer/parent/index.cjs.js');

const DEVICE_MODES = [
  { value: 'phone', label: 'Phone' },
  { value: 'tablet', label: 'Tablet' },
  { value: 'desktop', label: 'Desktop' }
];

const CWB_FONT_LINK_ID = 'cwb-preview-font-link';

const googleFontHref = (family) =>
  `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, '+')}:wght@400;600;700&display=swap`;

// Bake the parent's stylesheets into the iframe's initial document so
// Bootstrap and customCheckout.css are present from the first paint.
// Cloning <link href> uses the absolute URL the browser resolved on the
// parent, so relative paths work regardless of the iframe's base URL.
//
// The empty <div> is the default mount target react-frame-component
// looks up via `doc.body.children[0]` — without it, Frame silently
// renders nothing. The iframe-resizer child UMD bundle auto-initializes
// on script load (sets up the postMessage listener and posts Ready to
// parent). Absolute URL is required because srcdoc iframes resolve
// relative URLs against `about:srcdoc`, not the parent origin.
const buildInitialContent = () => {
  const head = Array.from(
    document.head.querySelectorAll('link[rel="stylesheet"], style')
  )
    .map(node => {
      if (node.tagName === 'LINK') {
        return `<link rel="stylesheet" href="${node.href}" />`;
      }
      return `<style>${node.innerHTML}</style>`;
    })
    .join('');

  const childScriptUrl = `${window.location.origin}/iframe-resizer-child.js`;
  return `<!DOCTYPE html><html><head>${head}<script src="${childScriptUrl}"></script></head><body style="margin:0;background:transparent"><div></div></body></html>`;
};

const PreviewFrame = ({
  confirm,
  widgetConfig,
  checkoutCategories,
  ticketTypes,
  addOns,
  deviceMode,
  onDeviceModeChange,
  webOrderFormProps,
  replacement
}) => {
  // Capture the parent's stylesheets once so the iframe's initialContent
  // is stable and doesn't force a Frame remount on every parent re-render.
  const initialContent = useMemo(buildInitialContent, []);

  // Connect iframe-resizer (parent → child via postMessage) so the iframe
  // element auto-grows with content height. waitForLoad gives the child
  // script time to boot before the parent starts polling.
  const iframeRef = useRef(null);
  useEffect(() => {
    if (!iframeRef.current) return undefined;
    const [resizer] = iframeResize(
      { license: '12ajjdewwwy-26rnhw2943-1s7g1u8ma0i', waitForLoad: true, log: false },
      iframeRef.current
    );
    return () => resizer && resizer.disconnect && resizer.disconnect();
  }, []);

  const [frameLoaded, setFrameLoaded] = useState(false);
  useEffect(() => {
    const node = iframeRef.current;
    if (!node) return undefined;
    const handleLoad = () => setFrameLoaded(true);
    node.addEventListener('load', handleLoad);
    return () => node.removeEventListener('load', handleLoad);
  }, []);

  const fontFamily = widgetConfig && widgetConfig.font_family;
  useEffect(() => {
    if (!frameLoaded) return;
    const frameDoc = iframeRef.current && iframeRef.current.contentDocument;
    if (!frameDoc || !frameDoc.head) return;

    const existingLink = frameDoc.getElementById(CWB_FONT_LINK_ID);
    if (!fontFamily) {
      if (existingLink) existingLink.remove();
      return;
    }

    const href = googleFontHref(fontFamily);
    if (existingLink) {
      existingLink.setAttribute('href', href);
      return;
    }

    const link = frameDoc.createElement('link');
    link.id = CWB_FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = href;
    frameDoc.head.appendChild(link);
  }, [frameLoaded, fontFamily]);

  // Server pre-renders the widget props on the ticketing page load (via
  // ConfirmTicketingPresenter#web_order_form_props) and ships them through
  // Redux, so the preview can render immediately without an axios round
  // trip. Live admin edits to widgetConfig/checkoutCategories override the
  // initial server snapshot.
  const mergedProps = webOrderFormProps && {
    ...webOrderFormProps,
    widgetConfig: widgetConfig || webOrderFormProps.widgetConfig,
    checkoutCategories: checkoutCategories || webOrderFormProps.checkoutCategories,
    ticketTypes: ticketTypes || webOrderFormProps.ticketTypes,
    addOns: addOns || webOrderFormProps.addOns,
    widgetType: 'custom',
    adminPreview: true
  };
  const loadError = false;

  const publicUrl = (confirm && confirm.public_ticketing_url) || '';
  const displayUrl = publicUrl.replace(/^https?:\/\//, '');

  // Outer device-frame body bg matches the user-picked widget background so
  // there's never a white gap during a color change. The iframe body is
  // transparent so this color shows through cleanly.
  const widgetBg = (widgetConfig && widgetConfig.background_color) || '#ffffff';
  const widgetFontColor = (widgetConfig && widgetConfig.font_color) || '#333333';

  return (
    <div className="preview-frame d-flex flex-column align-items-center" style={{ width: '100%' }}>
      <div className="cwb-device-switcher">
        <Tabs
          aria-label="Preview device"
          variant="button-border"
          size="sm"
          collapseBelow={0}
          items={DEVICE_MODES}
          value={deviceMode}
          onChange={onDeviceModeChange}
        />
      </div>

      <div className={`cwb-device-frame ${deviceMode}`}>
        <div className="cwb-device-frame-bar">
          <div className="cwb-frame-dots">
            <span className="cwb-frame-dot" />
            <span className="cwb-frame-dot" />
            <span className="cwb-frame-dot" />
          </div>
          <div className="cwb-frame-url">{displayUrl}</div>
        </div>

        <div className="cwb-device-frame-body" style={{ background: widgetBg, position: 'relative', minHeight: !mergedProps ? '300px' : undefined }}>
          {replacement && <div className="cwb-preview-replacement">{replacement}</div>}
          <Frame
            hidden={Boolean(replacement)}
            className="cwb-preview-iframe"
            initialContent={initialContent}
            ref={iframeRef}>
            {mergedProps && <WebOrderFormApp {...mergedProps} />}
          </Frame>
          {!replacement && (!mergedProps || loadError) && (
            <div className="cwb-preview-overlay" style={{ background: widgetBg, color: widgetFontColor }}>
              {loadError ? 'Failed to load preview. Try refreshing the page.' : 'Loading preview…'}
            </div>
          )}
        </div>
      </div>

      <div className="cwb-preview-disclaimer">
        For design purposes, every ticket type and add-on is shown here regardless of availability, sale window, or required-ticket rules. The live widget will hide items that don't apply.
      </div>
    </div>
  );
};

export default PreviewFrame;
